import React from 'react'
import { AlertTriangle, CheckCircle } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import type { DocumentReadiness } from './useDocumentReadiness'

interface DocumentReadinessPanelProps {
  readiness: DocumentReadiness[]
}

export const DocumentReadinessPanel: React.FC<DocumentReadinessPanelProps> = ({ readiness }) => {
  const allReady = readiness.every(r => r.ready)

  if (allReady) {
    return (
      <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
        <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0" aria-hidden="true" />
        <p className="text-sm text-green-700 dark:text-green-300 font-medium">All required fields are ready to sign</p>
      </div>
    )
  }

  const allMissing = readiness.flatMap(r =>
    r.missing.map(field => ({ field, documentName: r.documentName }))
  )

  return (
    <Card className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-amber-800 dark:text-amber-200 mb-2">
            Complete the following before signing:
          </p>
          <ul className="space-y-1">
            {allMissing.map((item, index) => (
              <li key={index} className="text-sm text-amber-700 dark:text-amber-300 flex items-start gap-1.5">
                <span className="flex-shrink-0 mt-0.5">•</span>
                <span>
                  <span className="font-medium">{item.field.description}</span>
                  {item.field.sourceStep && (
                    <span className="text-amber-600 dark:text-amber-400">
                      {' '}({hubLabel(item.field.sourceHub)} → {item.field.sourceStep})
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Card>
  )
}

function hubLabel(hub: string): string {
  const labels: Record<string, string> = {
    sales: 'Sales Hub',
    identification: 'ID Hub',
    purchase: 'Purchase Hub',
    disbursement: 'Disbursement Hub',
    computed: 'System',
  }
  return labels[hub] ?? hub
}
